import React from "react";
import { Modal, Button, Table, Badge, Card, Row, Col } from "react-bootstrap";
import { MecordColors } from "Constants/Color";
import SyncIcon from "@mui/icons-material/Sync";
import LockIcon from "@mui/icons-material/Lock";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface FetchQuotationDiffModalProps {
  show: boolean;
  onHide: () => void;
  diffData: any;
  onApply: (previewItems: any[]) => void;
}

export const FetchQuotationDiffModal: React.FC<FetchQuotationDiffModalProps> = ({
  show,
  onHide,
  diffData,
  onApply,
}) => {
  if (!diffData) return null;

  const { diff_summary, preview_service_items, quotation_number } = diffData;
  const lockedCount = diff_summary?.locked?.length || 0;
  const modifiedCount = diff_summary?.modified?.length || 0;
  const newCount = diff_summary?.new?.length || 0;
  const removedCount = diff_summary?.removed?.length || 0;
  const unchangedCount = diff_summary?.unchanged?.length || 0;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NEW":
        return <Badge bg="success"><AddCircleIcon style={{ fontSize: 14 }} /> NEW</Badge>;
      case "MODIFIED":
        return <Badge bg="warning" text="dark"><EditIcon style={{ fontSize: 14 }} /> MODIFIED</Badge>;
      case "REMOVED":
        return <Badge bg="danger"><DeleteIcon style={{ fontSize: 14 }} /> REMOVED</Badge>;
      case "LOCKED":
        return <Badge bg="secondary"><LockIcon style={{ fontSize: 14 }} /> LOCKED (Challan Generated)</Badge>;
      default:
        return <Badge bg="info"><CheckCircleIcon style={{ fontSize: 14 }} /> UNCHANGED</Badge>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton style={{ backgroundColor: MecordColors.ThemeBGColor, color: "white" }}>
        <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
          <SyncIcon className="me-2" />
          Review Latest Quotation Sync — Quotation #{quotation_number || ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Card className="mb-3 border-0 bg-light p-3">
          <h6 className="fw-bold mb-2">Summary of Quotation Line Changes</h6>
          <Row className="text-center g-2">
            <Col><Card className="p-2 border-0 bg-white shadow-sm"><strong className="text-success">{newCount}</strong> New Lines</Card></Col>
            <Col><Card className="p-2 border-0 bg-white shadow-sm"><strong className="text-warning">{modifiedCount}</strong> Modified Lines</Card></Col>
            <Col><Card className="p-2 border-0 bg-white shadow-sm"><strong className="text-danger">{removedCount}</strong> Removed Lines</Card></Col>
            <Col><Card className="p-2 border-0 bg-white shadow-sm"><strong className="text-secondary">{lockedCount}</strong> Locked Lines</Card></Col>
            <Col><Card className="p-2 border-0 bg-white shadow-sm"><strong className="text-info">{unchangedCount}</strong> Unchanged / Preserved</Card></Col>
          </Row>
        </Card>

        <h6 className="fw-bold mb-2">Service Line Item Comparison</h6>
        <Table responsive bordered hover className="align-middle" style={{ fontSize: "13px" }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: "50px" }}>#</th>
              <th>Service Name</th>
              <th>Model Name</th>
              <th>Quantity</th>
              <th>Unit Rate</th>
              <th>Total Amount</th>
              <th>Sync Status</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {(preview_service_items || [])
              .filter((item: any) => !item.is_accessory)
              .map((item: any, idx: number) => {
                const isRemoved = item.diff_status === "REMOVED";
                const accessoriesCount = Array.isArray(item.accessories) ? item.accessories.length : 0;
                return (
                  <tr key={idx} className={isRemoved ? "table-danger" : item.diff_status === "LOCKED" ? "table-secondary" : ""}>
                    <td>{idx + 1}</td>
                    <td className="fw-medium">
                      {item.product_name || item.serviceName || item.item_name || "-"}
                      {accessoriesCount > 0 && (
                        <Badge bg="light" text="dark" className="ms-2 border">
                          {accessoriesCount} {accessoriesCount === 1 ? "Accessory" : "Accessories"}
                        </Badge>
                      )}
                    </td>
                    <td>{item.model_name || item.model || "-"}</td>
                    <td>{item.product_quantity ?? item.quantity ?? 1}</td>
                    <td>₹{Number(item.unit_price || item.service_rate || 0).toLocaleString("en-IN")}</td>
                    <td>₹{Number(item.total_service_amount || item.gross_value || 0).toLocaleString("en-IN")}</td>
                    <td>{getStatusBadge(item.diff_status)}</td>
                    <td style={{ fontSize: "12px" }}>
                      {item.diff_status === "LOCKED" && "Protected by Challan. Will not be updated or deleted."}
                      {item.diff_status === "NEW" && "Added to quotation. Will be added to this Sales Order."}
                      {item.diff_status === "MODIFIED" && "Values updated from latest quotation."}
                      {item.diff_status === "REMOVED" && "Removed from quotation. Will be removed from order on save."}
                      {item.diff_status === "UNCHANGED" && (!item.quotation_service_item_id ? "Order-only service. Preserved." : "Matches quotation.")}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          style={{ backgroundColor: MecordColors.ThemeBGColor, borderColor: MecordColors.ThemeBGColor }}
          onClick={() => onApply(preview_service_items)}
        >
          Apply & Update Order Form
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
